import { createElement, useEffect, useState } from "react";
import { HelpComponentType } from "../../navigation/HelpNavigator";
import modalState from "../../state-observables/modal/ModalState";
import openNewTab from "../../utils/open-new-tab";
import styles from "./styles.module.css";
import SimpleButton from "../../components/disposables/buttons/SimpleButton";
import Logo from "../../components/disposables/Logo";
import AnalyticsConsentModal from "../../components/modals/AnalyticsConsentModal";
import HelpScreenText from "../../components/oneoffs/HelpScreenText";
import { VisitedScreenEnum } from "@robotical/appv2-analytics-gatherer/dist/types/types";
import { NewRobotIdE, useConnectedRafts } from "../../store/SelectedRaftContext";
import { getEmailGivenSerialNumber, getSerialNumberStubbornly, isSerialNumberRegistered } from "../../utils/warranty-service/warranty-service-utils";
import ServiceProgramOnboardingModal from "../../components/modals/ServiceProgramOnboardingModal";
import LoadingSpinner from "../../components/disposables/LoadingSpinner";
import ReportIssueModal from "../../components/modals/ReportIssueModal";
import { useTranslation } from "react-i18next";

interface HelpScreenProps extends HelpComponentType { }

export default function HelpScreen({ currentScreenSetter }: HelpScreenProps) {
  const { t } = useTranslation();
  const [shouldShowUsersDashboardButton, setShouldShowUsersDashboardButton] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [serialNumberEmail, setSerialNumberEmail] = useState<string | null>(null);
  const { connectedRafts } = useConnectedRafts();
  const currentlySelectedRobot = connectedRafts.find(connectedRaft => connectedRaft.isSelected && connectedRaft.id !== NewRobotIdE.NEW);
  const selectedRaftObj = window.applicationManager.connectedRafts[currentlySelectedRobot?.id || ""];

  useEffect(() => {
    // check if the selected robot is registered in the service program 
    setShouldShowUsersDashboardButton(true);
    setIsLoading(true);
    if (selectedRaftObj) {
      isSerialNumberRegistered(selectedRaftObj.getSerialNumber()).then((isRegistered) => {
        if (isRegistered) {
          // get the email associated with the serial number
          getEmailGivenSerialNumber(selectedRaftObj.getSerialNumber()).then((email) => {
            if (email) {
              // open the dasbhoard as a webview
              setSerialNumberEmail(email);
              setShouldShowUsersDashboardButton(true);
            }
          });
        } else {
          setShouldShowUsersDashboardButton(isRegistered);
        }
      }).catch((err) => console.log(err)).finally(() => setIsLoading(false));
    } else {
      setShouldShowUsersDashboardButton(true);
      setIsLoading(false);
    }
  }, [selectedRaftObj]);


  useEffect(() => {
    let stopHeartbeat: (() => void) | null = null;
    const logVisit = async () => {
      stopHeartbeat = await window.applicationManager.AnalyticsFacade.logScreenVisit(VisitedScreenEnum.HELP);
    };
    logVisit();

    return () => {
      if (stopHeartbeat) {
        stopHeartbeat();
      }
    };
  }, []);

  const onServiceProgram = async () => {
    showServiceProgramModal();
  }

  const showServiceProgramModal = () => {
    modalState.setModal(createElement(ServiceProgramOnboardingModal), "");
  }

  return (
    <div className={styles.helpScreenContainer}>
      <div className={styles.helpScreenLogoContainer}>
        <Logo />
      </div>
      <div className={styles.helpScreenMainTextContainer}>
        <HelpScreenText />
      </div>
      <div className={styles.helpScreenButtonsContainer}>
        <SimpleButton
          onClick={() =>
            openNewTab("https://userguides.robotical.io/martyv2/support_faqs")
          }
          title={t('help.open_faq')}
        />
        <SimpleButton
          onClick={() => openNewTab("https://codemarty.com/privacy-policy")}
          title={t('help.privacy_policy')}
        />
        <SimpleButton
          onClick={() => modalState.setModal(createElement(AnalyticsConsentModal), t('help.analytics_modal_title'))}
          title={t('help.data_analytics')}
        />
        <SimpleButton title={t('help.issue_report')} onClick={() => modalState.setModal(createElement(ReportIssueModal), t('help.issue_report'))} />
        {isLoading ? <div style={{ width: 30, justifySelf: "center" }}> <LoadingSpinner /></div> : <>
          {shouldShowUsersDashboardButton
            ?
            <SimpleButton title={t('help.warranty_dashboard')} onClick={() => serialNumberEmail ? currentScreenSetter("warranty-dashboard", { userEmail: serialNumberEmail }) : openNewTab("https://service-program-dashboard.robotical.io/")} />
            :
            <SimpleButton title={t('help.warranty_registration')} onClick={onServiceProgram} />
          }</>}
        {shouldShowUsersDashboardButton && <SimpleButton title={t('help.warranty_registration')} onClick={onServiceProgram} />}
      </div>
    </div>
  );
}
